/* eslint-disable react/no-unknown-property */
/* eslint-disable react/prop-types */
import React, { useMemo, useRef, useState } from "react";
import axios from "axios";
import ReactSelect, { components } from "react-select";
import JSZip from "jszip";

import ClientModal from "./client_modal";
import { turnXMLFormatToOurDataFormat, xml2json } from "./utility_functions";

const turnXMLStringIntoObjectArray = (xmlString) => {
  // turn string to xml
  const parser = new DOMParser();
  const xml = parser.parseFromString(xmlString, "text/xml");
  // turn xml into object array
  const jsonString = xml2json(xml);
  const json = JSON.parse(jsonString.replace("undefined", ""));
  return turnXMLFormatToOurDataFormat(json);
};

const generateTestId = (id) => {
  return `clients-page-import-export-modal-${id}`;
};

const Option = (props) => {
  const innerProps = {
    ...props.innerProps,
    "aria-selected": props.isSelected,
    role: "option",
    explorator_test_id: generateTestId(`location-select-option-${props.label}`)
  };

  return (
    <components.Option {...props} innerProps={innerProps}>
      <span>{props.label}</span>
    </components.Option>
  );
};

const MenuList = (props) => {
  const innerProps = {
    ...props.innerProps,
    role: "listbox"
  };

  return <components.MenuList {...props} innerProps={innerProps} />;
};

const ImportExportClientsModal = ({ locations, showButton = true }) => {
  const [showModal, setShowModal] = useState(false);
  const [duplicateClients, setDuplicateClients] = useState([]);
  const [newClients, setNewClients] = useState([]);

  const [message, setMessage] = useState();
  const [success, setSuccess] = useState();
  const [location, setLocation] = useState();
  const [selectedFileName, setSelectedFileName] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const openButtonRef = useRef(null);

  const showError = (message) => {
    setSuccess(false);
    setMessage(message);
    setDuplicateClients([]);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const fileNameSplit = file.name.split(".");
    const fileTypeExtension = fileNameSplit[fileNameSplit.length - 1].toLowerCase();

    setMessage(undefined);
    setDuplicateClients([]);
    setNewClients([]);
    setSelectedFileName(file.name);

    if (!(fileTypeExtension === "xml" || fileTypeExtension === "zip")) {
      showError("Choose an XML file or a ZIP file containing client XML records.");
      return;
    }
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        if (fileTypeExtension !== "xml") {
          let clients = [];
          const zip = await JSZip.loadAsync(file);
          const arrayOfFileKeys = Object.keys(zip.files);
          for (let fileNameIndex = 0; fileNameIndex < arrayOfFileKeys.length; fileNameIndex++) {
            const fileKey = arrayOfFileKeys[fileNameIndex];
            const fileName = zip.files[fileKey].name;
            // If statement deals with files that are OS/software specific
            if (
              !fileName.startsWith("__MACOSX/") &&
              !fileName.startsWith(".DS_Store") &&
              !fileName.startsWith("Thumbs.db") &&
              !fileName.startsWith("desktop.ini") &&
              !fileName.endsWith(".log") &&
              !fileName.endsWith(".txt") &&
              !fileName.startsWith(".")
            ) {
              try {
                const fileData = await zip.files[fileKey].async("string");
                let formattedData = turnXMLStringIntoObjectArray(fileData);
                clients = [...clients, ...formattedData];
              } catch (err) {
                showError(`Failed to load data from the file ${fileName}`);
              }
            }
          }
          setNewClients(clients);
        } else {
          try {
            const xmlString = reader.result;
            let formattedData = turnXMLStringIntoObjectArray(xmlString);
            formattedData = formattedData.map((ele) => ({ ...ele }));

            setNewClients(formattedData);
          } catch (err) {
            showError("The file could not be read. Choose a valid XML file or ZIP archive.");
          }
        }
      };
      await reader.readAsText(file);
    } catch (err) {
      showError("The file could not be read. Choose a valid XML file or ZIP archive.");
    }
  };

  const reset = () => {
    setShowModal(false);
    setMessage();
    setDuplicateClients([]);
    setNewClients([]);
    setLocation();
    setSelectedFileName("");
    setIsUploading(false);
    window.requestAnimationFrame(() => openButtonRef.current?.focus());
  };

  const uploadClients = async () => {
    try {
      if (!location) {
        showError("Select the location where these client records should be added.");
        return;
      }
      setIsUploading(true);
      const res = await axios.post("/clients/upload_clients.json", {
        clients: newClients,
        location: location.value
      });
      if (res.status >= 299) throw res.data.message;
      setShowModal(false);
      setMessage();
      setNewClients([]);
      setSuccess(true);
      window.location.reload();
    } catch (err) {
      console.error(err);
      if (err?.response?.data?.message) {
        showError(err.response.data.message);
        if (err.response.data.message.includes("Duplicate")) {
          setDuplicateClients(() => {
            return err.response.data.duplicate_clients;
          });
        }
      } else {
        showError(
          "The clients could not be imported. Try again, then ask an administrator for help."
        );
      }
    } finally {
      setIsUploading(false);
    }
  };

  const options = useMemo(() => {
    if (!locations) return [];
    return locations.map((location) => ({ value: location, label: location }));
  }, [locations]);

  const handleLocationChange = (selected) => {
    setLocation(selected);
  };

  return (
    <>
      <button
        type="button"
        className="button ccw-button ccw-button--secondary clients-page-import-export-modal-open-button"
        onClick={() => setShowModal(true)}
        ref={openButtonRef}
        explorator_test_id={generateTestId("open-modal-button")}
        id="client_page_header_import_clients"
        style={{
          display: showButton ? "block" : "none"
        }}
      >
        Import Clients
      </button>
      {showModal && (
        <ClientModal
          description="Upload client records and assign every record in this file to one CCW location."
          eyebrow="Client records"
          modifier="import"
          onClose={reset}
          title="Import Clients"
          titleTestId={generateTestId("title")}
          footer={
            <>
              <button className="ccw-button" onClick={reset} type="button">
                Cancel
              </button>
              <button
                className="button ccw-button ccw-button--primary clients-page-import-export-modal-upload-button"
                disabled={isUploading || !location || !(newClients && newClients.length > 0)}
                explorator_test_id={generateTestId("upload-file-button")}
                onClick={uploadClients}
                type="button"
              >
                {isUploading ? "Importing…" : "Import clients"}
              </button>
            </>
          }
        >
          <div className="upload-button-file-input-container ccw-client-import-controls">
            <div className="ccw-field ccw-client-import-file-field">
              <label className="ccw-field__label" htmlFor="client-import-file">
                Client file
              </label>
              <input
                accept=".xml,.zip,application/xml,application/zip"
                aria-describedby="client-import-file-help"
                className="ccw-client-import-file"
                explorator_test_id={generateTestId("upload-file-input")}
                id="client-import-file"
                name="import_file[attachment]"
                onChange={handleFileUpload}
                type="file"
              />
              <span className="ccw-field__help" id="client-import-file-help">
                Choose one XML file or a ZIP archive of XML files.
              </span>
            </div>

            <div className="ccw-field ccw-client-import-location-field">
              <label className="ccw-field__label" htmlFor="user_locations">
                Assign to location
              </label>
              <ReactSelect
                aria-describedby="client-import-location-help"
                className="ccw-client-import-location-select"
                classNamePrefix="ccw-client-select"
                closeMenuOnSelect={true}
                components={{ MenuList, Option }}
                hideSelectedOptions={false}
                inputId="user_locations"
                name="location"
                onChange={handleLocationChange}
                options={options}
                placeholder="Select a pickup location"
                required
                value={location}
              />
              <span className="ccw-field__help" id="client-import-location-help">
                This location applies to all records in the selected file.
              </span>
            </div>
          </div>

          {selectedFileName && (
            <div className="ccw-client-import-file-summary" role="status">
              <i aria-hidden="true" className="far fa-file-code" />
              <span>
                <strong>{selectedFileName}</strong>
                {newClients.length > 0 &&
                  ` · ${newClients.length} ${newClients.length === 1 ? "record" : "records"} ready`}
              </span>
            </div>
          )}

          {message && (
            <div
              aria-live="polite"
              className={`ccw-notice ${
                success ? "ccw-notice--success" : "ccw-notice--danger"
              } clients-page-import-export-modal-message`}
              role={success ? "status" : "alert"}
            >
              <strong>{success ? "Import complete" : "Import needs attention"}</strong>
              <p>{message}</p>
              {duplicateClients && duplicateClients.length > 0 && (
                <ul>
                  {duplicateClients.map((client, index) => (
                    <li key={`${client.first_name}-${client.last_name}-${index}`}>
                      {`${client.first_name} ${client.last_name}`}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {newClients && newClients.length > 0 && (
            <div className="import-modal-table-container client-body-in-modal ccw-client-modal__records">
              <div className="ccw-client-modal__records-heading">
                <h2>Records ready to import</h2>
                <span>{newClients.length} total</span>
              </div>
              <table className="ccw-client-modal__table">
                <caption className="is-sr-only">
                  Client records parsed from the selected import file
                </caption>
                <thead>
                  <tr className="clients-title">
                    <th>REG#</th>
                    <th>Client name</th>
                    <th>Location</th>
                    <th>Date of birth</th>
                    <th>Health card</th>
                  </tr>
                </thead>
                <tbody className="client-box">
                  {newClients.map((newClient, index) => (
                    <React.Fragment key={`${newClient.first_name}-${newClient.last_name}-${index}`}>
                      <tr className="card-container__top clients-page-import-export-modal-client-container">
                        <td data-label="REG#">{newClient.id || "—"}</td>
                        <td data-label="Client name">
                          <strong>{`${newClient.first_name} ${newClient.last_name}`}</strong>
                        </td>
                        <td data-label="Location">{newClient.location || "From file"}</td>
                        <td data-label="Date of birth">{newClient.dob || "—"}</td>
                        <td data-label="Health card">
                          <div className="ccw-client-modal__cell-content">
                            <span>{newClient.healthcard_number || "Not provided"}</span>
                            {(newClient.healthcard_version_code || newClient.healthcard_expiry) && (
                              <small>
                                {newClient.healthcard_version_code &&
                                  `Version ${newClient.healthcard_version_code}`}
                                {newClient.healthcard_version_code &&
                                  newClient.healthcard_expiry &&
                                  " · "}
                                {newClient.healthcard_expiry &&
                                  `Expires ${newClient.healthcard_expiry}`}
                              </small>
                            )}
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ClientModal>
      )}
    </>
  );
};

export default ImportExportClientsModal;
